import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import io from "socket.io-client";
import { FaSearch, FaCheckCircle, FaLightbulb, FaUsers, FaBolt } from "react-icons/fa";
import "./TextChatLobby.css";
import API_URL from '../../../utils/api';

const EQ_FACTOIDS = [
  {
    title: "MISSING NON-VERBAL CUES",
    fact: "Pay attention to the other person's Non-Verbal Cues like body language, facial expressions, tone of voice etc."
  },
  {
    title: "BEING IMPULSIVE",
    fact: "Think before you speak. Think before you act. Don't make insensitive comments. Control your actions."
  },
  {
    title: "LACKING SELF AWARENESS",
    fact: "Pay attention to your own feelings. Recognize what you are feeling, Recognize where the emotion came from, what triggered it. Choose carefully a suitable response behavior. Practice self-awareness."
  },
  {
    title: "BEING UNABLE TO HANDLE CRITICISM",
    fact: "See feedback as an opportunity for personal growth. Do not see it as a personal attack."
  },
  {
    title: "FAILURE TO MANAGE YOUR OWN EMOTIONS",
    fact: "Practice emotional regulation. Don't allow your emotions to dictate your actions. Don't lose your temper."
  },
  {
    title: "SUPPRESSING YOUR OWN EMOTIONS",
    fact: "Acknowledge what you are feeling, whether it is positive or negative. Deal with the feeling. Do not argue with it. Process, not suppress."
  },
  {
    title: "EXPECTING OTHER PEOPLE TO SUPPRESS THEIR OWN EMOTIONS",
    fact: "Recognize and respect that other people have their own emotions. Feelings are an inevitable part of human experience."
  },
  {
    title: "GIVING THE WRONG INTERPRETATION TO OTHER PEOPLE'S EMOTIONS",
    fact: "Recognize that other people experience complex emotions. Listen actively. Ask questions. Try to understand. Try to express compassion."
  },
  {
    title: "NOT PRACTISING EMPATHY BEHAVIOR",
    fact: "Empathy is about understanding and sharing other people's point of view and their feelings."
  },
  {
    title: "TRYING TO FIX A FRIEND'S FEELINGS WITH LOGIC",
    fact: "Be a witness to what your friend is feeling. Don't rush in with some solution that seems logical to you."
  },
  {
    title: "SHUTTING DOWN SOMEONE'S FEELINGS",
    fact: "Try to understand their point of view. Acknowledge what they are feeling. Don't say 'Get over it' or 'You're being too sensitive'. Respect what the other person is feeling. Express compassion."
  },
  {
    title: "TELLING THE OTHER PERSON WHAT THEY SHOULD DO",
    fact: "Listen actively to the other person. Engage with what they are feeling. Don't spit out your solutions to their situation like a candy vending machine gone crazy."
  },
  {
    title: "HIJACKING THE OTHER PERSON'S EMOTIONS",
    fact: "Do not say to the other person when they are being vulnerable 'You never listen to me' or 'You always cry when' or 'There you go again'. Recognize and respect."
  }
];

export default function VideoLobby() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentFactoid, setCurrentFactoid] = useState(0);
  const [isSearching, setIsSearching] = useState(true);
  const [matchFound, setMatchFound] = useState(false);
  const socketRef = useRef(null);
  const pollingRef = useRef(null);
  const userId = sessionStorage.getItem("userId") || localStorage.getItem("userId");
  const genderPreference = location.state?.genderPreference || 'random';

  // Rotate factoids every 8 seconds
  useEffect(() => {
    const factoidTimer = setInterval(() => {
      setCurrentFactoid((prev) => (prev + 1) % EQ_FACTOIDS.length);
    }, 8000);
    return () => clearInterval(factoidTimer);
  }, []);

  // Join video queue on mount
  useEffect(() => {
    const joinVideoQueue = async () => {
      try {
        const profileResponse = await fetch(`${API_URL}/api/profile/${userId}`);
        const profile = await profileResponse.json();

        const response = await fetch(`${API_URL}/api/funtellime/join-video-queue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            name: profile.name,
            interests: profile.interests,
            age: profile.age,
            location: profile.location,
            genderPreference: genderPreference
          })
        });

        const data = await response.json();

        if (data.matched) {
          setMatchFound(true);
          setIsSearching(false);
          
          setTimeout(() => {
            navigate(`/funtellime/video-profiles/${data.sessionId}`);
          }, 2000);
        } else {
          startPollingForMatch();
        }
      } catch (error) {
        alert("Failed to join video queue. Please try again.");
        navigate("/funtellime");
      }
    };

    joinVideoQueue();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      
      fetch(`${API_URL}/api/funtellime/cancel-video-queue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId })
      }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, navigate, genderPreference]);

  // Socket.io connection for real-time match notification
  useEffect(() => {
    socketRef.current = io(API_URL, {
      transports: ["websocket"],
      forceNew: true
    });

    socketRef.current.emit("funtellime-video-register", { userId });

    socketRef.current.on("funtellime-video-match-found", (data) => {
      setMatchFound(true);
      setIsSearching(false);
      
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }

      setTimeout(() => {
        navigate(`/funtellime/video-profiles/${data.sessionId}`);
      }, 2000);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit("funtellime-video-cancel-matching", { userId });
        socketRef.current.disconnect();
      }
    };
  }, [userId, navigate]);

  const startPollingForMatch = () => {
    pollingRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/funtellime/check-video-match/${userId}`);
        const data = await response.json();

        if (data.matched) {
          setMatchFound(true);
          setIsSearching(false);
          
          clearInterval(pollingRef.current);

          setTimeout(() => {
            navigate(`/funtellime/video-profiles/${data.sessionId}`);
          }, 2000);
        }
      } catch (error) {
      }
    }, 3000);
  };

  const handleCancel = async () => {
    setIsSearching(false);
    
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    await fetch(`${API_URL}/api/funtellime/cancel-video-queue`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId })
    }).catch(() => {});

    navigate("/funtellime");
  };

  return (
    <div className="text-lobby-container">
      <div className="text-lobby-content">
        <div className="search-status">
          <div className="search-animation">
            <div className="pulse-ring"></div>
            <div className="pulse-ring delay-1"></div>
            <div className="pulse-ring delay-2"></div>
            <div className="search-icon">{matchFound ? <FaCheckCircle /> : <FaSearch />}</div>
          </div>
          <h1>{matchFound ? "Match Found!" : "Finding Your Video Match..."}</h1>
          <p>{matchFound ? "Preparing your video session..." : "Matching you with someone based on shared interests"}</p>
        </div>

        {/* EQ Factoid Popup */}
        <div className="factoid-card">
          <div className="factoid-header">
            <span className="bulb-icon"><FaLightbulb /></span>
            <span className="factoid-label">Emotional Intelligence Tip</span>
          </div>
          <h3>{EQ_FACTOIDS[currentFactoid].title}</h3>
          <p>{EQ_FACTOIDS[currentFactoid].fact}</p>
          <div className="factoid-progress">
            {EQ_FACTOIDS.map((_, index) => (
              <div
                key={index}
                className={`progress-dot ${index === currentFactoid ? "active" : ""}`}
              />
            ))}
          </div>
        </div>

        <div className="lobby-info">
          <div className="info-item">
            <span className="info-icon"><FaUsers /></span>
            <span>Connecting via video chat</span>
          </div>
          <div className="info-item">
            <span className="info-icon"><FaBolt /></span>
            <span>AI-powered matching algorithm</span>
          </div>
        </div>

        {isSearching && (
          <button className="cancel-btn" onClick={handleCancel}>
            Cancel Search
          </button>
        )}
      </div>
    </div>
  );
}
